import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Hero from "@/components/Hero";
import Features from "@/components/Features";
import HowItWorks from "@/components/HowItWorks";
import TopCaregivers from "@/components/TopCaregivers";
import TrustPillars from "@/components/TrustPillars";

import BecomeSitterBanner from "@/components/BecomeSitterBanner";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://somoscompaws.com",
  "name": "Compaws",
  "description": "Plataforma de cuidadores de mascotas verificados en El Salvador. Conectamos dueños de mascotas con cuidadores de confianza para pet sitting, paseos y cuidado a domicilio.",
  "url": "https://somoscompaws.com",
  "logo": "https://somoscompaws.com/lovable-uploads/d4167bdc-fee8-46ee-82fb-52c880b10d47.webp",
  "image": "https://somoscompaws.com/lovable-uploads/d4167bdc-fee8-46ee-82fb-52c880b10d47.png",
  "email": "info@somoscompaws.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "San Salvador",
    "addressRegion": "San Salvador",
    "addressCountry": "SV"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 13.6929,
    "longitude": -89.2182
  },
  "areaServed": [
    { "@type": "City", "name": "San Salvador" },
    { "@type": "City", "name": "Antiguo Cuscatlán" },
    { "@type": "City", "name": "Santa Tecla" },
    { "@type": "City", "name": "San Miguel" }
  ],
  "serviceType": ["Pet sitting", "Cuidado de mascotas a domicilio", "Paseo de perros", "Hospedaje de mascotas"],
  "priceRange": "$$",
  "sameAs": [
    "https://www.instagram.com/com.paws"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Servicios de cuidado de mascotas en El Salvador",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pet sitting en casa" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Paseo de perros" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hospedaje de mascotas" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cuidado a domicilio" } }
    ]
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "¿Cómo funcionan los cuidadores de mascotas en Compaws?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "En Compaws, todos los cuidadores (llamados Compawdres) pasan por verificación de DUI salvadoreño, revisión de antecedentes y entrenamiento en cuidado de mascotas. Puedes ver el perfil, calificaciones y reseñas de cada cuidador antes de reservar. Operamos en San Salvador, Antiguo Cuscatlán, Santa Tecla y otras ciudades de El Salvador."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cuánto cuesta el pet sitting en El Salvador?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "En Compaws los precios varían por servicio. El pet sitting a domicilio, los paseos de perros y el hospedaje tienen diferentes tarifas accesibles para El Salvador. Todos los pagos se realizan de forma segura a través de la plataforma."
      }
    },
    {
      "@type": "Question",
      "name": "¿Los cuidadores de Compaws están verificados?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí. Todos los Compawdres son verificados con DUI salvadoreño, pasan revisión de antecedentes y reciben entrenamiento básico en cuidado de mascotas antes de poder ofrecer servicios en la plataforma."
      }
    },
    {
      "@type": "Question",
      "name": "¿En qué ciudades de El Salvador opera Compaws?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Compaws opera en San Salvador, Antiguo Cuscatlán, Santa Tecla, San Marcos, Soyapango y municipios del Gran San Salvador. Estamos en expansión hacia San Miguel, Sonsonate y La Libertad."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cómo me registro en Compaws como dueño de mascota?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Puedes registrarte en somoscompaws.com creando un perfil gratuito como dueño de mascota (Humano). Agrega los datos de tu mascota, busca cuidadores disponibles en tu zona de El Salvador, y reserva directamente desde la plataforma."
      }
    }
  ]
};

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.hash]);
  return (
    <div className="min-h-screen scroll-smooth">
      <SEOHead
        title="Compaws | Cuidadores de mascotas verificados en El Salvador"
        description="Encuentra cuidadores de mascotas verificados en El Salvador. Pet sitting, paseos y cuidado a domicilio en San Salvador, Antiguo Cuscatlán, Santa Tecla y más."
        canonical="https://somoscompaws.com"
        schema={[localBusinessSchema, faqSchema]}
      />
      <main>
        <Hero />
        <TopCaregivers />
        <Features />
        <HowItWorks />
        <TrustPillars />
        <BecomeSitterBanner />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
